import {
  DOOR_TYPES,
  animatedSheet,
  doorFrames,
  doorSheetOf,
  getMapObjects,
  type TiledMap,
} from '@veriverse/map-format';
import { type CollisionGrid } from '@veriverse/game-logic';
import { TILE_SIZE } from '@veriverse/shared';
import type Phaser from 'phaser';
import { parseObjectData } from './object-events.js';
import { spriteDepth } from './depth.js';
import { animatedTextureKey } from './animated-objects.js';

/** Mirrors `CoopDoorData` on the server: a door is open while `openUntil` is set. */
interface DoorData {
  readonly openUntil?: number;
  /** Set by plain toggle doors (ADR-0041). */
  readonly open?: boolean;
}

interface Door {
  readonly tiles: readonly { x: number; y: number }[];
  /** Plain slab, used when the door art did not load. */
  readonly slab?: Phaser.GameObjects.Rectangle;
  readonly sprites: readonly Phaser.GameObjects.Sprite[];
  readonly sheet: string;
  open?: boolean;
}

/** A door swings in about this long whatever its frame count. */
const SWING_SECONDS = 0.5;

const isDoor = (type: string) => DOOR_TYPES.includes(type);

/**
 * Door art (ADR-0043): any animated sheet with a closed and an open frame (`doorFrames`), set per door by the `door_sheet` property. Only the sheets a map uses are fetched.
 */
export function loadDoorArt(scene: Phaser.Scene, map: TiledMap): void {
  for (const object of getMapObjects(map)) {
    if (!isDoor(object.type)) continue;
    const sheet = doorSheetOf(object.properties);
    const spec = animatedSheet(sheet);
    const key = animatedTextureKey(sheet);
    if (!spec || scene.textures.exists(key)) continue;
    scene.load.spritesheet(key, `${import.meta.env.BASE_URL}animated/${sheet}.png`, {
      frameWidth: spec.width * TILE_SIZE,
      frameHeight: spec.height * TILE_SIZE,
    });
  }
}

const animKey = (sheet: string, direction: 'open' | 'close') => `door:${sheet}:${direction}`;

/** Forward frames for opening, the same frames reversed for closing; each plays once. */
function ensureDoorAnimations(scene: Phaser.Scene, sheet: string): void {
  if (scene.anims.exists(animKey(sheet, 'open'))) return;
  const { closed, open } = doorFrames(sheet);
  const step = open >= closed ? 1 : -1;
  const list = Array.from({ length: Math.abs(open - closed) + 1 }, (_, i) => closed + i * step);
  const frames = scene.anims.generateFrameNumbers(animatedTextureKey(sheet), { frames: list });
  const frameCount = list.length;
  const frameRate = Math.min(24, Math.max(8, Math.round(frameCount / SWING_SECONDS)));
  scene.anims.create({ key: animKey(sheet, 'open'), frames, frameRate });
  scene.anims.create({
    key: animKey(sheet, 'close'),
    frames: [...frames].reverse(),
    frameRate,
  });
}

/**
 * The co-op doors of the map (ADR-0038): a slab over the footprint and blocked tiles while the
 * door is closed; both go away while the server says it is open. The collision grid is the
 * client's own copy, so walking and click-to-move follow the same state as the server's.
 */
export function addCoopDoors(
  scene: Phaser.Scene,
  map: TiledMap,
  grid: CollisionGrid,
  reducedMotion = false,
) {
  const doors = new Map<number, Door>();
  for (const object of getMapObjects(map)) {
    if (!isDoor(object.type)) continue;
    const { x, y, w, h } = object.footprint;
    const tiles = Array.from({ length: w * h }, (_, i) => ({
      x: x + (i % w),
      y: y + Math.floor(i / w),
    }));
    const sheet = doorSheetOf(object.properties);
    const spec = animatedSheet(sheet);
    if (spec && scene.textures.exists(animatedTextureKey(sheet))) {
      ensureDoorAnimations(scene, sheet);
      // The picture stands on the footprint's bottom-left corner, repeated across a wide footprint.
      const copies = Math.max(1, Math.floor(w / spec.width));
      const sprites = Array.from({ length: copies }, (_, i) =>
        scene.add
          .sprite(
            (x + i * spec.width) * TILE_SIZE,
            (y + h) * TILE_SIZE,
            animatedTextureKey(sheet),
            doorFrames(sheet).closed,
          )
          .setOrigin(0, 1)
          .setDepth(spriteDepth(y + h - 1)),
      );
      doors.set(object.id, { tiles, sprites, sheet });
      continue;
    }
    const slab = scene.add
      .rectangle(x * TILE_SIZE, y * TILE_SIZE, w * TILE_SIZE, h * TILE_SIZE, 0x4b4f5c)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xd9a441)
      .setDepth(spriteDepth(y + h - 1));
    doors.set(object.id, { tiles, slab, sprites: [], sheet });
  }
  // The state that first arrives may already hold open doors; those appear open without a swing.
  let synced = false;
  const apply = (objects: Map<string, { data?: string }> | undefined) => {
    for (const [id, door] of doors) {
      const data = parseObjectData<DoorData>(objects?.get(String(id))?.data);
      const open = data?.openUntil !== undefined || data?.open === true;
      door.slab?.setVisible(!open);
      if (door.open !== open) {
        // The first sync only sets the picture; later changes play the swing.
        const animate = synced && !reducedMotion;
        const frames = doorFrames(door.sheet);
        for (const sprite of door.sprites) {
          if (animate) sprite.play(animKey(door.sheet, open ? 'open' : 'close'));
          else sprite.setFrame(open ? frames.open : frames.closed);
        }
        door.open = open;
      }
      for (const tile of door.tiles) grid.setBlocked(tile, !open);
    }
  };
  apply(undefined);
  return {
    /** Follows the room state's `objects`. */
    sync: (state: unknown) => {
      const objects = (state as { objects?: Map<string, { data?: string }> } | undefined)?.objects;
      apply(objects);
      if (objects) synced = true;
    },
    count: doors.size,
  };
}
